import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, ChevronLeft, ChevronRight, Share2, Instagram, Crown, UserPlus } from "lucide-react";
import InstagramCard from "@/components/InstagramCard";
import HeadToHead from "@/components/HeadToHead";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtShortDate } from "@/lib/format";
import { EmptyState, Spinner, ErrorBox } from "@/components/Bits";
import Leaderboard from "@/components/Leaderboard";
import AdSlot from "@/components/AdSlot";
import { PHASES, Select } from "@/lib/periods";
import { shareOrCopy, signed } from "@/lib/share";

const VIEWS = [["general", "Général"], ["week", "Semaine"], ["month", "Mois"], ["quarter", "Trimestre"], ["semester", "Semestre"], ["year", "Année"], ["nba_season", "Saison NBA"]];

export function StandingsTab({ league }) {
  const [view, setView] = useState("general");
  const [offset, setOffset] = useState(0);
  const [phase, setPhase] = useState("full");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [igOpen, setIgOpen] = useState(false);
  useEffect(() => { if (!league.observer) { api.post(`/leagues/${league.id}/checklist/standings`).catch(() => {}); } }, [league.id, league.observer]);
  useEffect(() => {
    setData(null);
    api.get(`/leagues/${league.id}/standings`, { params: { period: view, offset, phase } }).then(({ data }) => setData(data)).catch(() => setError("Classement indisponible."));
  }, [league.id, view, offset, phase]);
  const pick = (v) => { setView(v); setOffset(0); };
  const share = () => shareOrCopy({ title: league.name, text: `${league.name} — ${data.period?.label || "classement général"} : ${data.rows.filter((r) => r.rank).slice(0, 5).map((r) => `${r.rank}. ${r.display_name} (${signed(r.points)})`).join(", ")}` });
  return (
    <div className="space-y-4">
      <div className="mpl-scroll-x flex gap-1.5 overflow-x-auto">
        {VIEWS.map(([k, l]) => (
          <button key={k} onClick={() => pick(k)} data-testid={`standings-view-${k}`}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${view === k ? "border-[#B7F52A] bg-[#B7F52A] text-[#05070D]" : "mpl-pill border-white/10 text-slate-300 hover:border-white/25"}`}>{l}</button>
        ))}
      </div>
      {view !== "general" && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-[#05070D] p-1">
            <button onClick={() => setOffset(offset - 1)} aria-label="Période précédente" data-testid="standings-prev" className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/5"><ChevronLeft size={16} /></button>
            <span className="px-2 text-sm font-bold" data-testid="standings-period-label">{data?.period?.label || "…"}</span>
            <button onClick={() => setOffset(Math.min(0, offset + 1))} disabled={offset === 0} aria-label="Période suivante" data-testid="standings-next" className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/5 disabled:opacity-30"><ChevronRight size={16} /></button>
          </div>
          {view === "nba_season" && <div className="w-56"><Select value={phase} onChange={setPhase} options={PHASES} testId="standings-phase" /></div>}
        </div>
      )}
      {error && <ErrorBox message={error} />}
      {!data && !error && <Spinner />}
      {league.member_invites && !league.observer && (
        <p className="flex items-center gap-2 rounded-xl border border-[#B7F52A]/20 bg-[#B7F52A]/[0.07] p-3 text-sm text-slate-100" data-testid="standings-referral">
          <UserPlus size={15} className="shrink-0 text-[#B7F52A]" />
          <span>Parrainage : <b className="text-[#B7F52A]">+{league.referral_points} pts</b> au classement pour chaque nouveau joueur qui rejoint la ligue avec ton lien perso (onglet Aperçu).</span>
        </p>
      )}
      {data?.king?.user_ids?.length > 0 && (
        <p className="flex items-center gap-2 rounded-xl border border-amber-300/20 bg-amber-300/[0.07] p-3 text-sm text-amber-100" data-testid="standings-king">
          <Crown size={15} className="shrink-0" fill="currentColor" />
          <span>Roi de la semaine (du {data.king.week}) : <b>{data.rows.filter((r) => r.king).map((r) => r.display_name).join(", ")}</b>, {data.king.nights} soirée{data.king.nights > 1 ? "s" : ""} gagnée{data.king.nights > 1 ? "s" : ""}.</span>
        </p>
      )}
      {data && (data.rows.some((r) => r.resolved) || view === "general" ? (
        <>
          <Leaderboard rows={data.rows} leagueId={league.id} />
          <AdSlot placement="below_standings" leagueId={league.id} compact />
          <div className="flex flex-wrap gap-2">
            <button onClick={share} className="mpl-btn-ghost text-xs" data-testid="standings-share"><Share2 size={13} /> Partager le classement</button>
            <button onClick={() => setIgOpen(true)} className="mpl-btn-ghost text-xs" data-testid="standings-instagram"><Instagram size={13} /> Carte Instagram de la semaine</button>
          </div>
        </>
      ) : <EmptyState icon={BarChart3} title="Aucun résultat sur cette période" text="Les points apparaissent après confirmation des résultats officiels." testId="standings-empty" />)}
      <InstagramCard league={league} open={igOpen} onOpenChange={setIgOpen} />
    </div>
  );
}

const TYPE_LABEL = (r) => (r.type === "correct" ? "Bonne prédiction" : r.type === "incorrect" ? "Prédiction manquée" : r.type.startsWith("streak_bonus") ? `Bonus série de ${r.streak}` : r.type === "featured" ? "Bonus match vedette" : `Bonus : ${r.question}`);

export function MemberBreakdown({ league, memberId }) {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get(`/leagues/${league.id}/standings/${memberId}`).then(({ data }) => setData(data)); }, [league.id, memberId]);
  if (!data) return <Spinner />;
  return (
    <div data-testid="member-breakdown">
      <Link to={`/ligues/${league.id}/classement`} className="mpl-eyebrow mb-3 inline-block hover:text-white">← Classement</Link>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="break-words font-display text-2xl font-extrabold uppercase">{data.member.display_name}</h2>
          {data.member.username && <p className="text-sm font-semibold text-[#B7F52A]" data-testid="member-page-username">@{data.member.username}</p>}
        </div>
        {memberId !== user.id && !league.archived && <Link to={`/ligues/${league.id}/defis?adversaire=${memberId}`} className="mpl-btn-primary" data-testid="member-page-challenge">Défier {data.member.username ? `@${data.member.username}` : data.member.display_name}</Link>}
      </div>
      {memberId !== user.id && <HeadToHead leagueId={league.id} memberId={memberId} />}
      <p className="mb-4 mt-4 text-xs text-slate-400">Journal détaillé des points dans cette ligue.</p>
      {data.records.length ? (
        <div className="mpl-card divide-y divide-white/[0.05]">
          {data.records.map((r) => (
            <div key={`${r.game_id}-${r.type}`} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
              <div className="min-w-0"><p className="truncate font-semibold">{r.game.away.abbr} @ {r.game.home.abbr} · {TYPE_LABEL(r)}</p><p className="text-xs text-slate-500">{fmtShortDate(r.game.start_time)}</p></div>
              <span className={`font-mono-num font-bold ${r.points > 0 ? "text-[#B7F52A]" : r.points < 0 ? "text-red-300" : "text-slate-500"}`}>{signed(r.points)}</span>
            </div>
          ))}
        </div>
      ) : <EmptyState icon={BarChart3} title="Aucun point pour l'instant" text="Les points apparaissent après confirmation des résultats officiels." testId="breakdown-empty" />}
    </div>
  );
}
